import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AppContext } from "../../App";
import { gtThanZero, hasErrors, required, validate, validateAll } from "../../components/Validators";
import BaseDialog from "../../components/BaseDialog";
import { Alert, Box, Button } from "@mui/material";
import { calculateValueFromEvent, toComboValues } from "../../components/Utils";
import TextField from "../../components/TextField";
import NumberField from "../../components/NumberField";
import CheckBox from "../../components/CheckBox";
import ComboSelect from "../../components/ComboSelect";
import useApi from "../../api";
import LoadingDialog from "../../components/LoadingDialog";
import { registrarNodo } from "../../api/nodos";
import { SERVICIO_NODO, SIZE_NODO } from "./ListadoNodos";

const toServicioComboValues = () => SERVICIO_NODO.map(s => ({ value: s.id, text: s.nombre, disabled: s.disabled || false }));

const initialData = {
    servicio: 'AWS',
    cloudInstanceId: '',
    size: '',
    espacioDisponible: '',
    reservado: false,
}

const defaultErrors = {
    servicio: '',
    cloudInstanceId: '',
    size: '',
    espacioDisponible: '',
}

const fieldsValidators = {
    'servicio': [required],
    'cloudInstanceId': [required],
    'size': [required],
    'espacioDisponible': [required, gtThanZero({ msg: 'Debe ser mayor a cero' })],
}

const RegistrarNodoDialog = ({ ...props }) => {

    let { state } = useLocation();
    const navigate = useNavigate();

    const { notificar } = useContext(AppContext);

    const [data, setData] = useState({ ...initialData });
    const [errors, setErrors] = useState(defaultErrors);
    const [dirty, setDirty] = useState(false);
    const [isValid, setIsValid] = useState(false);
    const [open, setOpen] = useState(true);

    const [{ data: registrarNodoData, loading: loadingRegistrarNodo }, registrarNodoApi] = useApi();

    useEffect(() => {
        if (registrarNodoData) {
            notificar("Nodo registrado correctamente", { closeable: true });
            setOpen(false);
            if (props.onShouldUpdate) { //Avisamos por si quieren actualizar algo...
                props.onShouldUpdate();
            }
        }
    }, [registrarNodoData]);

    useEffect(() => {
        if (dirty === true) {
            const validationErrors = validateAll(data, fieldsValidators);
            setIsValid(!hasErrors(validationErrors));
        }
    }, [data, dirty]);

    const handleChangeValue = name => event => {
        let value = calculateValueFromEvent(event);
        if (name === 'cloudInstanceId' && typeof value === 'string') {
            value = value.trim();
        }
        const error = validate(value, fieldsValidators[name]);

        setData({
            ...data,
            [name]: value
        });
        setErrors({
            ...errors,
            [name]: error
        });
        setDirty(true);
    }

    const handleOnBlur = name => event => {
        const error = validate(event.target.value, fieldsValidators[name]);
        setErrors({
            ...errors,
            [name]: error
        });
    }

    const handleRegistrar = () => {
        if (isValid) {
            registrarNodoApi(registrarNodo(data));
        }
    }

    const handleExited = () => {
        navigate("..", { state: { ...state } });
    };

    const handleClose = () => {
        setOpen(false);
    };

    return (
        <>
            <BaseDialog open={open} onExited={handleExited} title="Registrar nodo existente" onClose={handleClose} fullWidth maxWidth="sm"
                actions={
                    <>
                        <Button onClick={handleClose} color="primary">Cancelar</Button>
                        <Button onClick={handleRegistrar} color="primary" variant="contained" disabled={!isValid}>Registrar</Button>
                    </>
                }>
                <Box>
                    <ComboSelect
                        fullWidth
                        required
                        field="servicio"
                        label="Servicio de nube"
                        value={data.servicio}
                        onChange={handleChangeValue('servicio')}
                        onBlur={handleOnBlur('servicio')}
                        errors={errors}
                        options={toServicioComboValues()}
                    />
                    <TextField
                        fullWidth
                        required
                        field="cloudInstanceId"
                        label="Cloud Instance ID"
                        placeholder="i-0123456789abcdef"
                        value={data.cloudInstanceId}
                        onChange={handleChangeValue('cloudInstanceId')}
                        onBlur={handleOnBlur('cloudInstanceId')}
                        errors={errors}
                    />
                    <ComboSelect
                        fullWidth
                        required
                        field="size"
                        label="Tamaño del nodo"
                        value={data.size}
                        onChange={handleChangeValue('size')}
                        onBlur={handleOnBlur('size')}
                        errors={errors}
                        options={toComboValues(SIZE_NODO)}
                    />
                    <NumberField
                        fullWidth
                        required
                        field="espacioDisponible"
                        label="Espacio disponible"
                        value={data.espacioDisponible}
                        onChange={handleChangeValue('espacioDisponible')}
                        onBlur={handleOnBlur('espacioDisponible')}
                        errors={errors}
                        formatter={{ decimalScale: 0, allowNegative: false }}
                    />
                    <CheckBox
                        label="Reservado (excluir de la asignación automática de instancias)"
                        field="reservado"
                        checked={data.reservado}
                        onChange={handleChangeValue('reservado')}
                    />
                    <Alert severity="info" sx={{ mt: 2 }}>
                        El host debe existir en AWS y su etiqueta <strong>Name</strong> iniciar con "escolastia".
                    </Alert>
                </Box>
            </BaseDialog>
            <LoadingDialog open={loadingRegistrarNodo} />
        </>
    )
};

export default RegistrarNodoDialog;
